import React, { createContext, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ConfirmModal, Toasts, useModalContext } from '@fourthwall/components';
import { useFormik } from '@fourthwall/utils/dist/formik';
import { useCreateImageResources } from '@modules/Dashboard/Memberships/hooks/useCreateImageResources';
import { routing } from '@utils/routing';
import { useCurrentShopQuery } from '../../../../../hooks/useCurrentShopQuery';
import { useCreateSingleSeriesMutation } from '../../hooks/useCreateSingleSeriesMutation';
import { useDeleteSingleSeriesMutation } from '../../hooks/useDeleteSingleSeriesMutation';
import { useSingleSeriesQuery } from '../../hooks/useSingleSeriesQuery';
import { useUpdateSingleSeriesMutation } from '../../hooks/useUpdateSingleSeriesMutation';
import { getCreateSingleSeriesPayload, getUpdateSingleSeriesPayload } from '../../utils';
import { validationSchema } from './consts';
import { FormFields } from './types';
export const SingleSeriesContext = createContext(undefined);
export const SingleSeriesProvider = ({ children }) => {
    const { singleSeriesId } = useParams();
    const navigate = useNavigate();
    const { createImageResources: createCoverImageResource, isLoading: createCoverImageResourceIsLoading, } = useCreateImageResources();
    const { createImageResources: createThumbnailImageResource, isLoading: createThumbnailImageResourceIsLoading, } = useCreateImageResources();
    const modal = useModalContext();
    const { currentShopQuery } = useCurrentShopQuery();
    const { singleSeriesQuery } = useSingleSeriesQuery([
        {
            id: Number(singleSeriesId),
        },
    ], {
        enabled: !!singleSeriesId,
    });
    const { updateSingleSeriesMutation } = useUpdateSingleSeriesMutation();
    const { createSingleSeriesMutation } = useCreateSingleSeriesMutation();
    const { deleteSingleSeriesMutation } = useDeleteSingleSeriesMutation();
    const form = useFormik({
        initialValues: {
            [FormFields.title]: singleSeriesQuery.data?.title || '',
            [FormFields.description]: singleSeriesQuery.data?.description || '',
            [FormFields.coverImage]: singleSeriesQuery.data?.coverImage?.url || '',
            [FormFields.coverImageResourceId]: undefined,
            [FormFields.thumbnailImage]: singleSeriesQuery.data?.thumbnail?.url || '',
            [FormFields.thumbnailImageResourceId]: undefined,
        },
        onSubmit: async (values) => {
            if (singleSeriesId) {
                await updateSingleSeriesMutation.mutateAsync([
                    {
                        id: Number(singleSeriesId),
                    },
                    { ...getUpdateSingleSeriesPayload(values) },
                ], {
                    onSuccess: () => {
                        singleSeriesQuery.refetch();
                        Toasts.notify('Video series updated.', { type: 'info' });
                    },
                });
            }
            else {
                await createSingleSeriesMutation.mutateAsync([{ ...getCreateSingleSeriesPayload(values) }], {
                    onSuccess: () => {
                        Toasts.notify('Video series created.', { type: 'info' });
                        navigate(routing.memberships.series.self);
                    },
                });
            }
        },
        validationSchema,
        enableReinitialize: true,
    });
    const handleUploadCoverImage = useCallback(async (file) => {
        await createCoverImageResource({
            files: [file],
            onSuccess: (images) => {
                form.setFieldValue(FormFields.coverImage, images[0].resizedUrl);
                form.setFieldValue(FormFields.coverImageResourceId, images[0].id);
            },
        });
    }, [createCoverImageResource, form]);
    const handleUploadThumbnailImage = useCallback(async (file) => {
        await createThumbnailImageResource({
            files: [file],
            onSuccess: (images) => {
                form.setFieldValue(FormFields.thumbnailImage, images[0].resizedUrl);
                form.setFieldValue(FormFields.thumbnailImageResourceId, images[0].id);
            },
        });
    }, [createThumbnailImageResource, form]);
    const deleteSingleSeries = useCallback(() => {
        modal.open(ConfirmModal, {
            title: 'Are you sure you want to delete this video series?',
            text: 'Deleting this video series will permanently remove it, so members can no longer view it. This action will not delete any of your videos. You cannot undo this.',
            confirmLabel: 'Delete series',
            confirmAppearance: 'destructive',
            onConfirm: () => {
                deleteSingleSeriesMutation.mutate([{ id: Number(singleSeriesId) }], {
                    onSuccess: () => {
                        navigate(routing.memberships.series.self);
                    },
                });
            },
        });
    }, [modal, deleteSingleSeriesMutation, navigate, singleSeriesId]);
    const value = useMemo(() => ({
        singleSeries: singleSeriesQuery.data,
        form,
        singleSeriesLink: currentShopQuery.data
            ? `${currentShopQuery.data.baseUri}/supporters/videos/series/${singleSeriesId}`
            : undefined,
        handleUploadCoverImage,
        handleUploadThumbnailImage,
        deleteSingleSeries,
        isLoading: singleSeriesQuery.isFetching ||
            createCoverImageResourceIsLoading ||
            createThumbnailImageResourceIsLoading ||
            updateSingleSeriesMutation.isLoading ||
            createSingleSeriesMutation.isLoading ||
            deleteSingleSeriesMutation.isLoading,
        isCoverImageUploading: createCoverImageResourceIsLoading,
        isThumbnailImageUploading: createThumbnailImageResourceIsLoading,
    }), [
        singleSeriesQuery,
        form,
        currentShopQuery.data,
        singleSeriesId,
        handleUploadCoverImage,
        handleUploadThumbnailImage,
        deleteSingleSeries,
        createCoverImageResourceIsLoading,
        createThumbnailImageResourceIsLoading,
        updateSingleSeriesMutation.isLoading,
        createSingleSeriesMutation.isLoading,
        deleteSingleSeriesMutation.isLoading,
    ]);
    return React.createElement(SingleSeriesContext.Provider, { value: value }, children);
};
